import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarIcon, Plus, Clock, MapPin, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import Calendar from 'react-calendar';

const DashboardMeetings = ({
  meetings, users, user,
  selectedDate, setSelectedDate,
  getMeetingsForDate, upcomingMeetings,
  onCreateMeeting, onUpdateStatus, onDeleteMeeting,
  dialogOpen, setDialogOpen,
  formData, setFormData,
}) => {
  const getStatusBadge = (status) => {
    const badges = {
      scheduled: <Badge className="bg-blue-100 text-blue-700">Planifie</Badge>,
      completed: <Badge className="bg-green-100 text-green-700">Termine</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-700">Annule</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const participantOptions = useMemo(() => (users || [])
    .filter(u => (formData.meeting_type === 'employee' ? u.role === 'employee' : u.role === 'client'))
    .filter(u => !formData.participant_ids.includes(u.id)), [users, formData.meeting_type, formData.participant_ids]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-6 w-6 text-red-600" />
              Agenda & Calendrier
            </CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-red-600 to-blue-600">
                  <Plus className="mr-2 h-5 w-5" /> Nouveau Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Creer un Meeting</DialogTitle>
                </DialogHeader>
                <form onSubmit={onCreateMeeting} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Heure *</Label>
                      <Input type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duree (min)</Label>
                      <Input type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={formData.meeting_type} onValueChange={(val) => setFormData({ ...formData, meeting_type: val, participant_ids: [] })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="employee">Employe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Lieu</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Bureau, Zoom, etc." />
                  </div>
                  <div className="space-y-2">
                    <Label>Participants</Label>
                    <Select onValueChange={(val) => setFormData({ ...formData, participant_ids: [...formData.participant_ids, val] })}>
                      <SelectTrigger><SelectValue placeholder="Ajouter un participant" /></SelectTrigger>
                      <SelectContent>
                        {participantOptions
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {formData.participant_ids.map(id => {
                      const p = (users || []).find(u => u.id === id);
                      return p ? (
                        <Badge key={id} className="mr-1 cursor-pointer" onClick={() => setFormData({ ...formData, participant_ids: formData.participant_ids.filter(pid => pid !== id) })}>
                          {p.full_name} x
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-blue-600">
                    Creer le Meeting
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale="fr-FR"
            className="rounded-lg border-0"
            tileContent={({ date }) => { // eslint-disable-line react/no-unstable-nested-components
              const dayMeetings = getMeetingsForDate(date);
              return dayMeetings.length > 0 ? (
                <div className="flex justify-center mt-1">
                  <div className="h-1.5 w-1.5 bg-red-600 rounded-full"></div>
                </div>
              ) : null;
            }}
          />
          {selectedDate && (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">
                Meetings du {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
              </h4>
              {getMeetingsForDate(selectedDate).length > 0 ? (
                <div className="space-y-2">
                  {getMeetingsForDate(selectedDate).map(meeting => (
                    <div key={meeting.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{meeting.title}</p>
                          <p className="text-sm text-gray-600">
                            <Clock className="inline h-3 w-3 mr-1" />
                            {meeting.time} ({meeting.duration}min)
                          </p>
                        </div>
                        {getStatusBadge(meeting.status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Aucun meeting</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Meetings */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle>Meetings a Venir</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {upcomingMeetings.length > 0 ? (
              upcomingMeetings.map(meeting => (
                <Card key={meeting.id} className="border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{meeting.title}</h4>
                        <p className="text-sm text-gray-600">{meeting.description}</p>
                      </div>
                      {getStatusBadge(meeting.status)}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 mb-3">
                      <p><CalendarIcon className="inline h-4 w-4 mr-1" />{format(parseISO(meeting.date), 'dd/MM/yyyy')} a {meeting.time}</p>
                      {meeting.location && <p><MapPin className="inline h-4 w-4 mr-1" />{meeting.location}</p>}
                    </div>
                    {user?.role === 'admin' && (
                      <div className="flex gap-2">
                        {meeting.status === 'scheduled' && (
                          <>
                            <Button size="sm" className="bg-green-600" onClick={() => onUpdateStatus(meeting.id, 'completed')}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Termine
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => onUpdateStatus(meeting.id, 'cancelled')}>
                              <XCircle className="h-4 w-4 mr-1" /> Annuler
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => onDeleteMeeting(meeting.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">Aucun meeting a venir</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardMeetings;
